/**
 * The monthly award to-do, seen from the client.
 *
 * netlify/functions/studentAwardsReminder.mjs writes one task per nominator
 * per round under the deterministic id
 * `studentAwards_{period}_{awardType}_{uid}` (its taskId()), the Director's
 * once-a-day send (studentAwardReminderNow.mjs) and a send-back
 * (studentAwardAction.mjs) write the same id, and the tasks create rule
 * refuses the prefix from any client (shared/firestore.rules
 * isServerOnlyTask).
 *
 * IT IS NEVER MARKED DONE BY HAND (Bobbie, 2026-11-01, DECISIONS 170). On
 * 2026-10-27 she reported "I she clicked done with this several times yet it
 * still shows as unread": the to-do's row offered Understood/complete, which
 * the rules accept, but a hand tick answers no nomination, and the server's
 * send-back path takes it off again. So every task row that offers a Done
 * control asks this file first and, for an award to-do, offers Open Student
 * Awards instead (taskActionMeta in lib/taskSources.ts already carries the
 * round's period on the link), with one line saying what clears it: her pick
 * (plus her Jr Staff MVP vote where the round asks for it,
 * lib/awardNominators.ts mvpJoinsAwardTodo), or No pick this month, which the
 * server records (studentAwardAction.mjs noPick). The same posture as
 * lib/attendanceNagTask.ts and lib/compMusicTask.ts.
 *
 * Firebase-free on purpose: lib/awardNominators.ts is the only import, so a
 * plain node test runs this (tests/award-todo-honesty.test.mjs) and
 * lib/notificationCompletion.ts, which has no Firebase of its own, can read
 * legacyAwardCardTaskIds.
 */
import { AWARD_TYPES, mvpJoinsAwardTodo, type AwardType } from './awardNominators.ts';

/** Mirrors taskId() in netlify/functions/studentAwardsReminder.mjs. */
export const AWARD_TASK_ID_PREFIX = 'studentAwards_';

export function awardTaskId(periodKey: string, awardType: AwardType, uid: string): string {
  return `${AWARD_TASK_ID_PREFIX}${periodKey}_${awardType}_${uid}`;
}

/**
 * The award period baked into an award reminder task's own deterministic id
 * (the inverse of awardTaskId), or null for any task id that was not built
 * by it. These reminder tasks carry no `source` field, so this is the only
 * provable destination they have: a task that stays open past the close day
 * must still point at ITS OWN round, not whatever month the Student Awards
 * page would otherwise default to that day.
 */
export function awardPeriodFromTaskId(taskId: string): string | null {
  const match = new RegExp(`^${AWARD_TASK_ID_PREFIX}(\\d{4}-\\d{2})_`).exec(taskId);
  return match ? match[1] : null;
}

/**
 * The award the to-do asks for, read off the same id, or null. The uid may
 * itself hold underscores, so the award is matched from the front: the
 * period, then one of the two award types, then the uid.
 */
export function awardTypeFromTaskId(taskId: string): AwardType | null {
  const period = awardPeriodFromTaskId(taskId);
  if (!period) return null;
  const rest = taskId.slice(`${AWARD_TASK_ID_PREFIX}${period}_`.length); // id-ok: reads the award type out of the to-do's own id; nothing here is shown as a name
  return AWARD_TYPES.find((awardType) => rest.startsWith(`${awardType}_`) && rest.length > awardType.length + 1) ?? null;
}

/** The fields of a task this file reads. Structural, so any task shape fits. */
export interface StudentAwardTaskLike {
  id: string;
}

/** True for the deterministic id the server mints for a nomination to-do; false for every other task. */
export function isStudentAwardTask(task: StudentAwardTaskLike): boolean {
  return awardPeriodFromTaskId(task.id) !== null;
}

/** The one button an award to-do row offers in place of Understood/complete or Mark done. */
export const AWARD_TODO_OPEN_LABEL = 'Open Student Awards';

/** The one plain line under that button where the round's to-do carries the Jr Staff MVP vote too. */
export const AWARD_TODO_SELF_CLEARS_WITH_MVP =
  'Your Dancer of the Month pick and your Jr Staff MVP vote check this off, or No pick this month. It is not marked done by hand.';

/** The same line for a to-do that asks for the pick alone. */
export const AWARD_TODO_SELF_CLEARS = 'Your pick checks this off, or No pick this month. It is not marked done by hand.';

/**
 * Which of the two lines this to-do gets: both halves where the round's
 * Student of the Month to-do also asks for the Jr Staff MVP vote (the October
 * 2026 round on), the pick alone for a Spotlight to-do and for every round
 * before that.
 */
export function awardTodoSelfClears(task: StudentAwardTaskLike): string {
  const period = awardPeriodFromTaskId(task.id);
  const awardType = awardTypeFromTaskId(task.id);
  return period && awardType && mvpJoinsAwardTodo(awardType, period)
    ? AWARD_TODO_SELF_CLEARS_WITH_MVP
    : AWARD_TODO_SELF_CLEARS;
}

/**
 * The to-dos a LEGACY award card can stand for (lib/notificationCompletion.ts
 * notificationMatchesTask). A reminder or came-back card written before
 * DECISIONS 170 carries kind 'task', a link to /student-awards?period=P and no
 * taskId, so the only thing that can bind it to a to-do is its own uid plus
 * the period in its link: exactly the two ids that uid could hold for that
 * round, never a prefix match, or a Director's card would fold behind a
 * stranger's row.
 */
export function legacyAwardCardTaskIds(period: string, uid: string): string[] {
  return AWARD_TYPES.map((awardType) => awardTaskId(period, awardType, uid));
}
